import { useState, useEffect, type FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { PlacementPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function PlacementEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [placement, setPlacement] = useState<PlacementPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getPlacement(id)
      .then((res) => {
        setPlacement(res.placement);
        setStatus(res.placement.status);
        setStartDate(res.placement.start_date);
        setEndDate(res.placement.end_date);
        setNotes(res.placement.notes ?? "");
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load placement"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.updatePlacement(id, {
        status: status as "reserved" | "active" | "expired" | "cancelled",
        start_date: startDate,
        end_date: endDate,
        notes: notes || null,
      });
      navigate(`/placements/${id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to update placement");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="placement-edit" data-state="loading">Loading...</p>;
  if (error && !placement) return <p className="text-sm text-destructive" data-page="placement-edit" data-state="error" data-error>{error}</p>;
  if (!placement) return <p className="text-muted-foreground" data-page="placement-edit" data-state="empty">Placement not found</p>;

  const selectClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="flex flex-col gap-6" data-page="placement-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Placement</h1>
        <p className="text-muted-foreground">
          {placement.sponsor_name} -- {placement.ad_space_name} ({placement.court_name})
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Placement Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="placement-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                data-field="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={selectClass}
              >
                <option value="reserved">Reserved</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                data-field="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                data-field="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                data-field="notes"
                placeholder="Optional notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link to={`/placements/${id}`} className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
